import { IdentityUserAri } from '@atlassian/ari/identity/user';

import { OPTIMISTIC_AGENT_REPLY_ID_PREFIX } from './agentMentionConstants.ts';

type Account = { accountId?: string | null };
export type ReplyMatchComment = {
	id: string;
	parentId: string | null;
	createdAtNonLocalized: string;
	author: Account;
	contributors: readonly (Account | null)[] | null;
};

export type PostedReplyMatch = {
	commentId: string;
	userId: string;
	agentId: string;
	startedAt: number;
	existingCommentIds: ReadonlySet<string>;
};

const threadRoot = (id: string, comments: Record<string, ReplyMatchComment>): string => {
	const visited = new Set<string>();
	let parentId = comments[id]?.parentId;
	while (parentId && !visited.has(id)) {
		visited.add(id);
		id = parentId;
		parentId = comments[id]?.parentId;
	}
	return id;
};

const isReplyFromAgentAndUser = (
	comment: ReplyMatchComment,
	match: Pick<PostedReplyMatch, 'agentId' | 'userId'>,
): boolean => {
	const author = comment.author.accountId;
	const contributors = comment.contributors ?? [];
	return (
		(author === match.agentId && contributors.some((user) => user?.accountId === match.userId)) ||
		(author === match.userId && contributors.some((user) => user?.accountId === match.agentId))
	);
};

const normalizeAgentId = (agentId: string): string => {
	// Session agents use identity ARIs; comment attribution uses account IDs.
	try {
		return IdentityUserAri.parse(agentId).userId;
	} catch {
		return agentId;
	}
};

// Display-only fallback after reload; attribution cannot distinguish repeated runs on one mention.
export function hasPostedCommentAgentReplyAfterMention(
	comments: Record<string, ReplyMatchComment>,
	match: Pick<PostedReplyMatch, 'commentId' | 'userId' | 'agentId'>,
): boolean {
	const source = comments[match.commentId];
	if (!source || source.id.startsWith(OPTIMISTIC_AGENT_REPLY_ID_PREFIX)) return false;
	const sourceCreatedAt = Date.parse(source.createdAtNonLocalized);
	const rootId = threadRoot(source.id, comments);
	const normalizedMatch = { ...match, agentId: normalizeAgentId(match.agentId) };
	return Object.values(comments).some((comment) => {
		if (comment.id === source.id || comment.id.startsWith(OPTIMISTIC_AGENT_REPLY_ID_PREFIX)) {
			return false;
		}
		if (!comment.parentId) return false;
		if (!(Date.parse(comment.createdAtNonLocalized) > sourceCreatedAt)) return false;
		if (!isReplyFromAgentAndUser(comment, normalizedMatch)) return false;
		return threadRoot(comment.id, comments) === rootId;
	});
}

export function findPostedCommentAgentReply(
	comments: Record<string, ReplyMatchComment>,
	match: PostedReplyMatch,
): ReplyMatchComment | undefined {
	const normalizedMatch = { ...match, agentId: normalizeAgentId(match.agentId) };
	const rootId = threadRoot(match.commentId, comments);
	return Object.values(comments).find((comment) => {
		if (comment.id.startsWith(OPTIMISTIC_AGENT_REPLY_ID_PREFIX)) return false;
		if (!comment.parentId || match.existingCommentIds.has(comment.id)) return false;
		// Exclude historical comments loaded later, allowing a small server/client clock difference.
		if (!(Date.parse(comment.createdAtNonLocalized) >= match.startedAt - 30_000)) return false;
		if (!isReplyFromAgentAndUser(comment, normalizedMatch)) return false;
		return threadRoot(comment.id, comments) === rootId;
	});
}

export function hasPostedCommentAgentReply(
	comments: Record<string, ReplyMatchComment>,
	match: PostedReplyMatch,
): boolean {
	return Boolean(findPostedCommentAgentReply(comments, match));
}
